import React, { useState, useEffect, lazy, Suspense } from 'react';
import './App.css';
import { ENV_CONFIG } from './config/environment';
import { ServicesManagement } from './components/services/ServicesManagement';
import { Monitoring } from './components/monitoring/Monitoring';
import Users from './components/users/Users';
import Analytics from './components/analytics/Analytics';
import DatabaseManager from './components/database/DatabaseManager';
import DeploymentManager from './components/deployment/DeploymentManager';

// Lazy load RAG components
const DocumentManager = lazy(() => import('./components/rag/DocumentManager'));
const RAGChat = lazy(() => import('./components/rag/RAGChat'));
const RAGSearch = lazy(() => import('./components/rag/RAGSearch'));
const CodeSearch = lazy(() => import('./components/rag/CodeSearch'));
const SuperCodeSearch = lazy(() => import('./components/rag/SuperCodeSearch'));
const RAGAnalytics = lazy(() => import('./components/rag/RAGAnalytics'));
const AdvancedSearch = lazy(() => import('./components/rag/AdvancedSearch'));

interface ServiceStatus {
  name: string;
  status: 'operational' | 'degraded' | 'down' | 'offline';
  port: number;
  url: string;
  lastCheck: string;
  response?: any;
}

interface SystemMetrics {
  totalServices: number;
  activeServices: number;
  activeAlerts: number;
  performance: number;
}

function App() {
  // Load active tab from localStorage or default to 'overview'
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('smart-ops-active-tab');
    return saved || 'overview';
  });
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('smart-ops-sidebar-collapsed');
    return saved === 'true';
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Real service data
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [systemStatus, setSystemStatus] = useState<'operational' | 'degraded' | 'down'>('operational');

  const navigationItems = [
    { id: 'overview', label: 'Overview', icon: '📊', color: '#3b82f6' },
    { id: 'chat', label: 'Chat AI', icon: '🤖', color: '#8b5cf6' },
    { id: 'services', label: 'Services', icon: '⚙️', color: '#10b981' },
    { id: 'monitoring', label: 'Monitoring', icon: '📈', color: '#f59e0b' },
    { id: 'users', label: 'Users', icon: '👥', color: '#8b5cf6' },
    { id: 'analytics', label: 'Analytics', icon: '📊', color: '#ef4444' },
    { id: 'data-analysis', label: 'Data Analysis', icon: '🔍', color: '#06b6d4' },
    { id: 'rag-documents', label: 'Documents', icon: '📄', color: '#3b82f6' },
    { id: 'rag-chat', label: 'RAG Chat', icon: '💬', color: '#8b5cf6' },
    { id: 'rag-search', label: 'RAG Search', icon: '🔍', color: '#10b981' },
    { id: 'code-search', label: 'Code Search', icon: '💻', color: '#06b6d4' },
    { id: 'super-code-search', label: 'Super Code Search', icon: '🚀', color: '#f97316' },
    { id: 'advanced-search', label: 'Advanced Search', icon: '⚡', color: '#ec4899' },
    { id: 'rag-analytics', label: 'Analytics', icon: '📊', color: '#f59e0b' },
    { id: 'database', label: 'Database', icon: '🗄️', color: '#06b6d4' },
    { id: 'deployment', label: 'Deployment', icon: '🚀', color: '#f97316' },
    { id: 'settings', label: 'Settings', icon: '⚙️', color: '#6b7280' },
  ];

  // Real service endpoints - only include services that are actually running
  const serviceEndpoints = [
    { name: 'Smart-Ops Backend', port: ENV_CONFIG.SMART_OPS_BACKEND_PORT, url: ENV_CONFIG.SMART_OPS_BACKEND_URL },
    { name: 'AI Service', port: ENV_CONFIG.AI_SERVICE_PORT, url: ENV_CONFIG.AI_SERVICE_URL },
    { name: 'RAG Service', port: ENV_CONFIG.RAG_SERVICE_PORT, url: ENV_CONFIG.RAG_SERVICE_URL },
  ];

  // Check service health with multiple endpoint attempts
  const checkServiceHealth = async (service: typeof serviceEndpoints[0]): Promise<ServiceStatus> => {
    try {
      const startTime = Date.now();
      const response = await fetch(`${service.url}/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(5000)
      });
      const endTime = Date.now();
      const responseTime = endTime - startTime;

      if (response.ok) {
        const data = await response.json();
        return {
          name: service.name,
          status: 'operational',
          port: service.port,
          url: service.url,
          lastCheck: new Date().toISOString(),
          response: { ...data, responseTime }
        };
      } else {
        return {
          name: service.name,
          status: 'degraded',
          port: service.port,
          url: service.url,
          lastCheck: new Date().toISOString(),
          response: { status: 'error', message: `HTTP ${response.status}` }
        };
      }
    } catch (error) {
      return {
        name: service.name,
        status: 'down',
        port: service.port,
        url: service.url,
        lastCheck: new Date().toISOString(),
        response: { status: 'error', message: error instanceof Error ? error.message : 'Unknown error' }
      };
    }
  };

  // Load real data
  const loadRealData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check all services
      const serviceChecks = await Promise.allSettled(
        serviceEndpoints.map(checkServiceHealth)
      );
      
      const serviceResults = serviceChecks.map(result => 
        result.status === 'fulfilled' ? result.value : {
          name: 'Unknown Service',
          status: 'offline' as const,
          port: 0,
          url: '',
          lastCheck: new Date().toISOString()
        }
      );
      
      setServices(serviceResults);
      
      // Calculate real metrics
      const onlineServices = serviceResults.filter(s => s.status === 'operational').length;
      const totalServices = serviceResults.length;
      const activeAlerts = serviceResults.filter(s => s.status === 'down').length;
      const performance = totalServices > 0 ? Math.round((onlineServices / totalServices) * 100) : 0;
      
      setMetrics({
        totalServices,
        activeServices: onlineServices,
        activeAlerts,
        performance
      });
      
      // Determine system status
      if (onlineServices === totalServices) {
        setSystemStatus('operational');
      } else if (onlineServices > 0) {
        setSystemStatus('degraded');
      } else {
        setSystemStatus('down');
      }
      
    } catch (err) {
      setError('Failed to load system data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Save active tab to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('smart-ops-active-tab', activeTab);
  }, [activeTab]);

  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('smart-ops-sidebar-collapsed', sidebarCollapsed.toString());
  }, [sidebarCollapsed]);

  // Load data on mount and refresh every 30 seconds
  useEffect(() => {
    loadRealData();
    const interval = setInterval(loadRealData, 30000);
    return () => clearInterval(interval);
  }, []);

  const renderOverviewContent = () => (
    <div className="overview-content" data-tab="overview">
      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading real system data...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button className="btn-primary" onClick={loadRealData}>Retry</button>
        </div>
      ) : (
        <>
          {/* Real Stats */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#3b82f6' }}>
                <span>🚀</span>
              </div>
              <div className="stat-content">
                <h3>Total Services</h3>
                <div className="stat-value">{metrics?.totalServices || 0}</div>
                <div className="stat-change neutral">Real-time</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#10b981' }}>
                <span>✅</span>
              </div>
              <div className="stat-content">
                <h3>Active Services</h3>
                <div className="stat-value">{metrics?.activeServices || 0}</div>
                <div className="stat-change neutral">Live data</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#f59e0b' }}>
                <span>⚠️</span>
              </div>
              <div className="stat-content">
                <h3>Offline Services</h3>
                <div className="stat-value">{metrics?.activeAlerts || 0}</div>
                <div className="stat-change neutral">Real alerts</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#8b5cf6' }}>
                <span>📈</span>
              </div>
              <div className="stat-content">
                <h3>System Health</h3>
                <div className="stat-value">{metrics?.performance || 0}%</div>
                <div className="stat-change neutral">Live metrics</div>
              </div>
            </div>
          </div>

          {/* Real Service Status */}
          <div className="content-section">
            <div className="section-header">
              <h2>Live Service Status</h2>
              <button className="btn-secondary" onClick={loadRealData} aria-label="Refresh service status">Refresh</button>
            </div>
            <div className="service-grid">
              {services.map((service, index) => (
                <div key={index} className="service-item">
                  <div className="service-info">
                    <div className="service-icon">
                      {service.status === 'operational' ? '🟢' : service.status === 'degraded' ? '🟡' : '🔴'}
                    </div>
                    <div>
                      <h4>{service.name}</h4>
                      <p>Port: {service.port}</p>
                      <small>Last check: {new Date(service.lastCheck).toLocaleTimeString()}</small>
                      {service.response && (
                        <small className="response-info">
                          Status: {service.response.status || service.response.message || 'OK'}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className={`service-status ${service.status}`}>
                    {service.status === 'operational' ? 'Online' : 
                     service.status === 'degraded' ? 'Degraded' : 'Offline'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* System Information */}
          <div className="content-section">
            <div className="section-header">
              <h2>System Information</h2>
            </div>
            <div className="system-info">
              <div className="info-item">
                <strong>System Status:</strong> 
                <span className={`status-badge ${systemStatus}`}>
                  {systemStatus === 'operational' ? '🟢 Operational' :
                   systemStatus === 'degraded' ? '🟡 Degraded' : '🔴 Down'}
                </span>
              </div>
              <div className="info-item">
                <strong>Last Updated:</strong> {new Date().toLocaleString()}
              </div>
              <div className="info-item">
                <strong>Data Source:</strong> Real API endpoints
              </div>
              <div className="info-item">
                <strong>Auto-refresh:</strong> Every 30 seconds
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderServicesContent = () => (
    <div className="services-content" data-tab="services">
      <ServicesManagement />
    </div>
  );

  const renderMonitoringContent = () => (
    <div className="monitoring-content" data-tab="monitoring">
      <Monitoring />
    </div>
  );

  const renderUsersContent = () => (
    <div className="users-content">
      <Users />
    </div>
  );

  const renderAnalyticsContent = () => (
    <div className="analytics-content">
      <Analytics />
    </div>
  );

  const renderDataAnalysisContent = () => {
    const DataAnalysis = React.lazy(() => import('./components/analytics/DataAnalysis'));
    return (
      <div className="data-analysis-content" data-tab="data-analysis">
        <React.Suspense fallback={<div>Loading Data Analysis...</div>}>
          <DataAnalysis />
        </React.Suspense>
      </div>
    );
  };

  const renderRAGDocumentsContent = () => (
    <div className="rag-documents-content" data-tab="rag-documents">
      <Suspense fallback={<div className="loading-state">Loading Document Manager...</div>}>
        <DocumentManager />
      </Suspense>
    </div>
  );

  const renderRAGChatContent = () => (
    <div className="rag-chat-content">
      <Suspense fallback={<div className="loading-state">Loading RAG Chat...</div>}>
        <RAGChat />
      </Suspense>
    </div>
  );

  const renderRAGSearchContent = () => (
    <div className="rag-search-content" data-tab="rag-search">
      <Suspense fallback={<div className="loading-state">Loading RAG Search...</div>}>
        <RAGSearch />
      </Suspense>
    </div>
  );

  const renderCodeSearchContent = () => (
    <div className="code-search-content" data-tab="code-search">
      <Suspense fallback={<div className="loading-state">Loading Code Search...</div>}>
        <CodeSearch />
      </Suspense>
    </div>
  );

  const renderSuperCodeSearchContent = () => (
    <div className="super-code-search-content" data-tab="super-code-search">
      <Suspense fallback={<div className="loading-state">Loading Super Code Search...</div>}>
        <SuperCodeSearch />
      </Suspense>
    </div>
  );

  const renderAdvancedSearchContent = () => (
    <div className="advanced-search-content">
      <Suspense fallback={<div className="loading-state">Loading Advanced Search...</div>}>
        <AdvancedSearch />
      </Suspense>
    </div>
  );

  const renderRAGAnalyticsContent = () => (
    <div className="rag-analytics-content">
      <Suspense fallback={<div className="loading-state">Loading RAG Analytics...</div>}>
        <RAGAnalytics />
      </Suspense>
    </div>
  );

  const renderChatContent = () => {
    const ChatPage = React.lazy(() => import('./pages/ChatPage'));
    return (
      <div className="chat-content" data-tab="chat">
        <React.Suspense fallback={<div>Loading Chat AI...</div>}>
          <ChatPage />
        </React.Suspense>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewContent();
      case 'chat':
        return renderChatContent();
      case 'services':
        return renderServicesContent();
      case 'monitoring':
        return renderMonitoringContent();
      case 'users':
        return renderUsersContent();
      case 'analytics':
        return renderAnalyticsContent();
      case 'data-analysis':
        return renderDataAnalysisContent();
      case 'rag-documents':
        return renderRAGDocumentsContent();
      case 'rag-chat':
        return renderRAGChatContent();
      case 'rag-search':
        return renderRAGSearchContent();
      case 'code-search':
        return renderCodeSearchContent();
      case 'super-code-search':
        return renderSuperCodeSearchContent();
      case 'advanced-search':
        return renderAdvancedSearchContent();
      case 'rag-analytics':
        return renderRAGAnalyticsContent();
      case 'database':
        return <DatabaseManager />;
      case 'deployment':
        return <DeploymentManager />;
      default:
        return (
          <div className="content-placeholder">
            <h2>{navigationItems.find(item => item.id === activeTab)?.label}</h2>
            <p>This section requires real API integration.</p>
            <p>No mock data available - only live services.</p>
          </div>
        );
    }
  };

  return (
    <div className="admin-dashboard">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <span className="logo-icon">🚀</span>
            {!sidebarCollapsed && <span className="logo-text">9G SmartOps</span>}
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!sidebarCollapsed}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>
        </div>
        
        <nav className="sidebar-nav" role="navigation" aria-label="Main navigation">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
              onClick={() => setActiveTab(item.id)}
              style={{ '--accent-color': item.color } as React.CSSProperties}
              aria-label={`Navigate to ${item.label}`}
              aria-pressed={activeTab === item.id}
              role="tab"
              data-tab={item.id}
            >
              <span className="nav-icon" aria-hidden="true">{item.icon}</span>
              {!sidebarCollapsed && <span className="nav-label">{item.label}</span>}
            </button>
          ))}
        </nav>
        
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">👤</div>
            {!sidebarCollapsed && (
              <div className="user-details">
                <span className="user-name">Admin User</span>
                <span className="user-role">Administrator</span>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <header className="top-header">
          <div className="header-left">
            <h1>{navigationItems.find(item => item.id === activeTab)?.label}</h1>
            <p>Real-time data from live services - No mock data</p>
          </div>
          <div className="header-right">
            <div className="system-status">
              <span className={`status-dot ${systemStatus}`}></span>
              <span>
                {systemStatus === 'operational' ? 'System Operational' :
                 systemStatus === 'degraded' ? 'System Degraded' : 'System Down'}
              </span>
            </div>
            <button className="btn-notification" onClick={loadRealData} aria-label="Refresh system data">🔄</button>
          </div>
        </header>

        <div className="content-area">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

export default App; 